/**
 * Extract a valid News ID from a news object or string
 */
export const getNewsId = (item) => {
    if (!item) return "";
    if (typeof item === "string" || typeof item === "number") return String(item).trim();
    return String(item._id || item.id || "").trim();
};

/**
 * Get API Base URL
 */
export const getApiBase = () => {
    let url = process.env.REACT_APP_API_URL || "https://proudtapi.onrender.com";
    url = url.replace(/\/$/, "");
    if (url.endsWith("/api")) url = url.slice(0, -4);
    return url;
};

/**
 * Get Public Frontend Base URL
 */
export const getFrontendBase = () => {
    // During local development share the local app URL, so links can be
    // opened and tested on the same machine.
    if (typeof window !== "undefined" && window.location?.hostname === "localhost") {
        return window.location.origin.replace(/\/$/, "");
    }

    // Production shares must use the public website domain. Do this before
    // window.location.origin because the app can also be opened from Render
    // (or a preview domain), which must never be shared as the article URL.
    const envUrl = process.env.REACT_APP_FRONTEND_URL;
    if (envUrl) return envUrl.replace(/\/$/, "");

    if (typeof window !== "undefined" && window.location && window.location.origin) {
        return window.location.origin.replace(/\/$/, "");
    }
    return "https://www.proudtapi.in";
};

/**
 * Get direct news details URL for sharing or opening
 * Example: https://www.proudtapi.in/news-details/6a86f37ce8bb130999134b98
 */
export const getNewsShareUrl = (item) => {
    const id = getNewsId(item);
    const frontendBase = getFrontendBase();
    if (!id) {
        return typeof window !== "undefined" && window.location?.href ? window.location.href : frontendBase;
    }
    return `${frontendBase}/news-details/${encodeURIComponent(id)}`;
};

/**
 * Get the URL used by social share actions.
 *
 * Every shared link must open the public News Details route.  Previously this
 * returned the API's `/news/share/:id` endpoint, which exposed that technical
 * URL in WhatsApp and could leave a reader on the API host instead of the
 * website.
 */
export const getNewsSocialShareUrl = (item) => {
    return getNewsShareUrl(item);
};

/**
 * Universal helper to copy news link to clipboard
 */
export const copyNewsLink = async (item) => {
    const url = getNewsShareUrl(item);
    try {
        if (typeof navigator !== "undefined" && navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(url);
            alert("લિંક કૉપિ થઈ ગઈ છે! (Link copied!)");
            return true;
        }
    } catch {
        // Fallback below
    }

    if (typeof window !== "undefined") {
        window.prompt("Copy this link:", url);
    }
    return true;
};

/**
 * Open external share dialog, app, or copy link
 */
export const shareNewsTo = (platform, item) => {
    if (!item) return;

    const url = platform === "copy" ? getNewsShareUrl(item) : getNewsSocialShareUrl(item);
    const title = (typeof item === "object" ? item?.title : "") || "ProudTapi News";
    const cleanTitle = title.trim();
    const shareText = `${cleanTitle}\n${url}`;

    const openTab = (targetUrl) => {
        if (typeof window !== "undefined") {
            window.open(targetUrl, "_blank", "noopener,noreferrer");
        }
    };

    switch (platform) {
        case "whatsapp":
            openTab(`https://wa.me/?text=${encodeURIComponent(shareText)}`);
            break;
        case "facebook":
            openTab(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`);
            break;
        case "x":
        case "twitter":
            openTab(`https://twitter.com/intent/tweet?text=${encodeURIComponent(cleanTitle)}&url=${encodeURIComponent(url)}`);
            break;
        case "telegram":
            openTab(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(cleanTitle)}`);
            break;
        case "linkedin":
            openTab(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`);
            break;
        case "instagram":
            openTab("https://www.instagram.com/proud_tapi_news");
            break;
        case "email":
            openTab(`mailto:?subject=${encodeURIComponent(cleanTitle)}&body=${encodeURIComponent(shareText)}`);
            break;
        case "copy":
            copyNewsLink(item);
            break;
        default:
            if (typeof navigator !== "undefined" && navigator.share) {
                navigator.share({ title: cleanTitle, text: cleanTitle, url }).catch(() => {});
            } else {
                openTab(`https://wa.me/?text=${encodeURIComponent(shareText)}`);
            }
            break;
    }
};

const shareUtils = {
    getNewsId,
    getApiBase,
    getFrontendBase,
    getNewsShareUrl,
    getNewsSocialShareUrl,
    copyNewsLink,
    shareNewsTo,
};

export default shareUtils;
